import { Title } from "lola-framework-ui-test";
import { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "./BodyCopy";
import { Popup } from "./Popup";
import { Toast } from "./Toast";
import { MailStrokeIcon, WhatsAppStrokeIcon } from "../assets/icons";
import { useStore } from "../store/globalState";
import { TITLE_COLOR } from "../constants";
import { useSessionStore } from "../store/useSessionStore";
import { RequestStatus } from "../utils/commons";
import { getEvents, safeCapture } from "../analytics";
import { CloseLight } from "../assets/navIcons";

const SUPPORT_SKELETON_ROWS = 3;

/** Shimmer rows while the session (and with it the partner channels) loads. */
const SupportChannelsSkeleton = ({ color }: { color: string }) => (
  <>
    {Array.from({ length: SUPPORT_SKELETON_ROWS }, (_, i) => (
      <div
        key={i}
        className="support-sheet-skeleton"
        style={{ color }}
        aria-hidden="true"
      >
        <span className="support-sheet-skeleton-icon" />
        <span className="support-sheet-skeleton-line" />
      </div>
    ))}
  </>
);

export interface SupportChannels {
  email?: string;
  whatsapp?: string;
}

export interface SupportSheetProps {
  visible: boolean;
  onClose: () => void;
  /**
   * When set, these channels replace the session. Both missing means the
   * list is already known to be empty, so the sheet does not wait on a load.
   */
  channels?: SupportChannels;
}

/**
 * Contact channels as a bottom sheet, opened from the "need help" affordance of
 * the screen the user is already on. Only the channels the partner configured
 * are offered.
 */
export const SupportSheet = memo(({ visible, onClose, channels }: SupportSheetProps) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { backgroundBtn, textColorBtn, bodyCopy } = theme.whiteView;
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    if (!visible) return;
    safeCapture(getEvents().SUPPORT_VIEW);
  }, [visible]);

  const whatsappNumber = channels
    ? channels.whatsapp
    : session.data?.partner?.supportWhatsappNumber;
  const supportEmail = channels
    ? channels.email
    : session.data?.partner?.supportEmail;

  const goToWhatsApp = useCallback(() => {
    if (!whatsappNumber) return;
    const cleanPhone = whatsappNumber.replace(/\D/g, "");
    window.open(`https://wa.me/${cleanPhone}`, "_blank");
  }, [whatsappNumber]);

  const sendEmail = useCallback(async () => {
    if (!supportEmail) return;
    try {
      await navigator.clipboard.writeText(supportEmail);
      setShowToast(true);
    } catch {
      // Clipboard may be unavailable; still open mail client.
    }
    window.open(`mailto:${supportEmail}`, "_blank");
  }, [supportEmail]);

  const hasChannels = Boolean(whatsappNumber || supportEmail);
  /* Channels already in the rehydrated session stay visible while it reloads;
     the skeleton only covers having nothing to offer yet. */
  const showSkeleton =
    !channels && session.status === RequestStatus.LOADING && !hasChannels;

  return (
    <>
      <Popup visible={visible} onClose={onClose}>
        <section className="support-sheet" aria-busy={showSkeleton}>
          <header className="payout-details-header">
            <span className="payout-header-action" aria-hidden="true" />
            <Title
              title={t("contact.chooseOption")}
              align="center"
              color={bodyCopy}
            />
            <button
              type="button"
              className="payout-header-action payout-close-btn"
              aria-label={t("common.cancel")}
              onClick={onClose}
            >
              <CloseLight size={36} colors={[bodyCopy, bodyCopy]} />
            </button>
          </header>
          {showSkeleton && <SupportChannelsSkeleton color={TITLE_COLOR} />}
          {!showSkeleton && supportEmail && (
            <button
              type="button"
              className="support-sheet-option"
              style={{ color: bodyCopy }}
              onClick={() => void sendEmail()}
            >
              <span
                className="support-sheet-option-icon"
                style={{ background: backgroundBtn }}
              >
                <MailStrokeIcon
                  size={22}
                  colors={[textColorBtn, textColorBtn]}
                />
              </span>
              <span style={{ color: TITLE_COLOR }}>{t("contact.email")}</span>
            </button>
          )}
          {!showSkeleton && whatsappNumber && (
            <button
              type="button"
              className="support-sheet-option"
              style={{ color: bodyCopy }}
              onClick={goToWhatsApp}
            >
              <span
                className="support-sheet-option-icon"
                style={{ background: backgroundBtn }}
              >
                <WhatsAppStrokeIcon
                  size={22}
                  colors={[textColorBtn, textColorBtn]}
                />
              </span>
              <span style={{ color: TITLE_COLOR }}>{t("contact.whatsapp")}</span>
            </button>
          )}
          {!showSkeleton && !hasChannels && (
            <BodyCopy className="footer-text muted-foreground">
              {t("contact.empty")}
            </BodyCopy>
          )}
        </section>
      </Popup>
      <section className="toast-container">
        {showToast && (
          <Toast
            message={t("contact.emailCopied")}
            duration={2000}
            type="success"
            onClose={() => setShowToast(false)}
          />
        )}
      </section>
    </>
  );
});
